import dateFnsGenerateConfig from 'rc-picker/lib/generate/dateFns';
import { Calendar, DatePicker } from 'antd';
import type { PickerProps } from 'antd/es/date-picker/generatePicker';
import * as React from 'react';

export interface TimePickerProps extends Omit<PickerProps<Date>, 'picker'> {}

/**
 * antd ships its picker locales written for DAYJS; these pickers are generated from DATE-FNS. Most
 * tokens happen to agree, and the ones that do not simply throw — date-fns rejects an unknown latin
 * letter rather than passing it through.
 *
 * `cellMeridiemFormat` is the one that bit us. antd's English locale sets it to `'A'`, which dayjs
 * reads as AM/PM and date-fns does not accept at all (`aa` is its spelling). rc-picker formats the
 * meridiem column with it, so opening ANY 12-hour time panel threw
 * `RangeError: Format string contains an unescaped latin alphabet character 'A'` from inside
 * `<TimePanelBody>` — the panel never rendered, and a therapist clicking the reminder time on the
 * coping-card form got nothing at all. Found 2026-09-08 driving `tools/portalCards.spec.ts`; it
 * never showed in Russian, where the format is 24-hour and there is no meridiem column to build.
 *
 * The map is deliberately whole-string only: these locale entries ARE a single token, and a
 * substring rewrite would corrupt a real format that happens to contain the letter.
 */
const DAYJS_ONLY_FORMATS: Record<string, string> = { A: 'aa' };

const dateFnsConfig = {
  ...dateFnsGenerateConfig,
  locale: {
    ...dateFnsGenerateConfig.locale,
    format: (locale: string, date: Date, format: string) =>
      dateFnsGenerateConfig.locale.format(
        locale,
        date,
        DAYJS_ONLY_FORMATS[format] ?? format,
      ),
  },
};

/**
 * N-10 (founder, 2026-10-22): generated from the SAME antd the app renders — `import { DatePicker } from 'antd'`,
 * never `antd/lib/...`.
 *
 * Until then these were built from `antd/lib/date-picker/generatePicker` (the CommonJS build) while every other antd
 * component comes from `antd` (webpack resolves it to the ES build). Those are two copies of antd with two separate
 * React contexts, so the pickers sat OUTSIDE the app's `ConfigProvider` (App.tsx): they rendered in antd's default
 * theme (system font, 32 px high, 6 px corners — beside the Serene Shell 44 px Lexend fields, the "misaligned" Due at
 * field), took no z-index from the Drawer or Modal around them (antd raises a popup above its container's mask through
 * a z-index context the CJS copy never reads; the CJS picker always opens at the base 1050), and kept their styles
 * under a default-theme token that nothing else in the app uses. `@ant-design/cssinjs` deletes EVERY style tag of a
 * token once its last user unmounts (`cleanTokenStyle` in `useCacheToken`) — a fate the app's own token, always
 * mounted, never meets — so the picker's whole stylesheet lives or dies with the pickers on screen. Without that sheet
 * the popup is an unpositioned block of plain numbers ("Oct2026", "Mo Tu We Th") at the foot of the page, under the
 * drawer's mask: what the founder saw.
 *
 * `DatePicker.generatePicker` / `Calendar.generateCalendar` are antd's public way to swap the date library; built here
 * they read the app's theme, locale, z-index and popup container like any other antd control. The theme class is
 * asserted by `pw-tests/N10-date-pickers.spec.ts`.
 */
const GeneratedDatePicker = DatePicker.generatePicker<Date>(dateFnsConfig);

type EscapeContainedProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>, preventDefault: () => void) => void;
};

/**
 * N-10 (found 2026-10-22): Escape in an OPEN picker closes the picker only.
 *
 * rc-picker closes its popup on Escape and lets the key bubble on; antd's Drawer (rc-drawer `onKeyDown`, ESC → `onClose`)
 * and Modal (rc-dialog, the same) are React ancestors of the field and close on it. So one Escape that a therapist meant
 * for the calendar also closed the Assign homework drawer — and, with `destroyOnClose`, threw the whole form away (or
 * raised the tool sections' "Discard your changes?"). The picker's own `onKeyDown` (antd's supported prop, called before
 * rc-picker acts on the key) stops the key's propagation while the popup is open; rc-picker still closes the popup,
 * because propagation is not `preventDefault`. With the popup closed nothing is stopped: Escape in the field closes the
 * drawer or asks to discard, as before.
 */
type AnyPickerProps = EscapeContainedProps & Record<string, unknown>;

const containEscape = (Picker: React.ComponentType<AnyPickerProps>, displayName: string) => {
  const Contained = React.forwardRef<unknown, AnyPickerProps>((props, ref) => {
    const { open, onOpenChange, onKeyDown } = props as EscapeContainedProps;
    const openRef = React.useRef(false);
    const handleOpenChange = React.useCallback(
      (next: boolean) => {
        openRef.current = next;
        onOpenChange?.(next);
      },
      [onOpenChange],
    );
    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLElement>, preventDefault: () => void) => {
        if (event.key === 'Escape' && (open ?? openRef.current)) event.stopPropagation();
        onKeyDown?.(event, preventDefault);
      },
      [open, onKeyDown],
    );
    const Inner = Picker as React.ComponentType<AnyPickerProps & { ref?: React.Ref<unknown> }>;
    return <Inner {...props} ref={ref} onOpenChange={handleOpenChange} onKeyDown={handleKeyDown} />;
  });
  Contained.displayName = displayName;
  return Contained;
};

type GeneratedStatics = Omit<typeof GeneratedDatePicker, keyof React.Component | 'prototype'>;
const containedStatic = <K extends keyof GeneratedStatics>(key: K, name: string) =>
  containEscape(
    GeneratedDatePicker[key] as unknown as React.ComponentType<AnyPickerProps>,
    name,
  ) as unknown as (typeof GeneratedDatePicker)[K];

export const DateFnsDatePicker = Object.assign(
  containEscape(
    GeneratedDatePicker as unknown as React.ComponentType<AnyPickerProps>,
    'DateFnsDatePicker',
  ),
  {
    WeekPicker: containedStatic('WeekPicker', 'DateFnsWeekPicker'),
    MonthPicker: containedStatic('MonthPicker', 'DateFnsMonthPicker'),
    YearPicker: containedStatic('YearPicker', 'DateFnsYearPicker'),
    QuarterPicker: containedStatic('QuarterPicker', 'DateFnsQuarterPicker'),
    TimePicker: containedStatic('TimePicker', 'DateFnsGeneratedTimePicker'),
    RangePicker: containedStatic('RangePicker', 'DateFnsRangePicker'),
  },
) as unknown as typeof GeneratedDatePicker;

export const DateFnsTimePicker = React.forwardRef<any, TimePickerProps>(
  (props, ref) => {
    return (
      // @ts-ignore
      <DateFnsDatePicker {...props} picker="time" mode={undefined} ref={ref} />
    );
  },
);

DateFnsTimePicker.displayName = 'TimePicker';

export const DateFnsCalendar = Calendar.generateCalendar<Date>(dateFnsConfig);
